/**
 * DistributionTab
 * Full lead distribution functionality embedded in dashboard tab
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import WeightConfigurator from '../../distribution/WeightConfigurator';
import DistributionPreview from '../../distribution/DistributionPreview';
import DistributionHistory from '../../distribution/DistributionHistory';
import LeadSelector from '../../distribution/LeadSelector';
import Spinner from '../../common/Spinner';
import TabNavigation from '../../common/TabNavigation';
import { hasDirectorAccess } from '../../../utils/rolePermissions';

export default function DistributionTab() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('distribute');

  // Distribution configuration
  const [poolType, setPoolType] = useState('new');
  const [leadType, setLeadType] = useState('team'); // For manager only
  const [recipients, setRecipients] = useState([]);
  const [weights, setWeights] = useState({});
  const [poolStats, setPoolStats] = useState({
    new: 0,
    old: 0,
    recycled: 0
  });

  // Manual selection state
  const [manualSelectionEnabled, setManualSelectionEnabled] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [poolLeads, setPoolLeads] = useState([]);
  const [loadingPoolLeads, setLoadingPoolLeads] = useState(false);

  // Preview
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // History
  const [history, setHistory] = useState([]);

  // Loading states
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExecute, setLoadingExecute] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingRollback, setLoadingRollback] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  // Determine distribution type based on role
  const isDirectorOrManager = hasDirectorAccess(user?.role) || user?.role === 'manager';
  const distributionType = isDirectorOrManager ? 'manager_to_leaders' : 'leader_to_agents';
  const recipientType = isDirectorOrManager ? 'leaders' : 'agents';

  // Fetch recipients when component mounts
  useEffect(() => {
    fetchRecipients();
  }, []);

  // Fetch pool stats when component mounts
  useEffect(() => {
    fetchPoolStats();
  }, []);

  // Fetch history when tab changes
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  // Refetch pool leads when poolType changes and manual selection is enabled
  useEffect(() => {
    if (manualSelectionEnabled) {
      fetchPoolLeads();
    }
  }, [poolType, manualSelectionEnabled]);

  async function fetchRecipients() {
    try {
      setLoadingRecipients(true);
      const response = await api.get(`/distribution/recipients?type=${recipientType}`);
      setRecipients(response.data.recipients);
    } catch (error) {
      console.error('Fetch recipients error:', error);
      toast.error('Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  }

  async function fetchPoolStats() {
    try {
      setLoadingStats(true);
      // Fetch stats for all pools
      const [newStats, oldStats, recycledStats] = await Promise.all([
        api.get('/distribution/pool-stats?poolType=new'),
        api.get('/distribution/pool-stats?poolType=old'),
        api.get('/distribution/pool-stats?poolType=recycled')
      ]);

      setPoolStats({
        new: newStats.data.availableLeads || 0,
        old: oldStats.data.availableLeads || 0,
        recycled: recycledStats.data.availableLeads || 0
      });
    } catch (error) {
      console.error('Fetch pool stats error:', error);
    } finally {
      setLoadingStats(false);
    }
  }

  async function fetchPoolLeads() {
    try {
      setLoadingPoolLeads(true);
      const params = new URLSearchParams({
        poolType,
        distributionType,
        ...(user.role === 'leader' && leadType ? { leadType } : {})
      });

      const response = await api.get(`/distribution/pool-leads?${params}`);
      setPoolLeads(response.data.leads || []);
    } catch (error) {
      console.error('Fetch pool leads error:', error);
      toast.error('Failed to load pool leads');
    } finally {
      setLoadingPoolLeads(false);
    }
  }

  async function fetchHistory() {
    try {
      setLoadingHistory(true);
      const response = await api.get('/distribution/history');
      setHistory(response.data.sessions);
    } catch (error) {
      console.error('Fetch history error:', error);
      toast.error('Failed to load distribution history');
    } finally {
      setLoadingHistory(false);
    }
  }

  async function handlePreview() {
    if (Object.keys(weights).length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    if (manualSelectionEnabled && selectedLeadIds.length === 0) {
      toast.error('Please select at least one lead');
      return;
    }

    try {
      setLoadingPreview(true);
      const response = await api.post('/distribution/preview', {
        distributionType,
        poolType,
        leadType: isDirectorOrManager ? leadType : null,
        weights,
        selectedLeadIds: manualSelectionEnabled ? selectedLeadIds : null
      });

      if (response.data.success) {
        setPreview(response.data);
        setShowPreview(true);
      } else {
        toast.warning(response.data.message);
        setPreview(null);
        setShowPreview(false);
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.message || 'Failed to preview distribution');
    } finally {
      setLoadingPreview(false);
    }
  }

  async function handleExecute() {
    try {
      setLoadingExecute(true);
      const response = await api.post('/distribution/execute', {
        distributionType,
        poolType,
        leadType: isDirectorOrManager ? leadType : null,
        weights,
        selectedLeadIds: manualSelectionEnabled ? selectedLeadIds : null
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowPreview(false);
        setPreview(null);
        setWeights({});
        setSelectedLeadIds([]);
        setManualSelectionEnabled(false);
        setPoolLeads([]);
        fetchPoolStats();
        // Switch to history tab to show the result
        setActiveTab('history');
        fetchHistory();
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      console.error('Execute error:', error);
      toast.error(error.response?.data?.message || 'Failed to execute distribution');
    } finally {
      setLoadingExecute(false);
    }
  }

  // This function is called FROM DistributionHistory after it completes a rollback
  // It should ONLY refresh the data, not trigger another rollback
  function handleRollbackComplete() {
    fetchHistory();
    fetchPoolStats();
  }

  const tabs = [
    { id: 'distribute', label: 'Distribute Leads' },
    { id: 'history', label: 'History' }
  ];

  return (
    <>
      {/* Tab Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-display font-bold text-gray-900">Lead Distribution</h2>
          <p className="text-gray-600 text-sm">
            {isDirectorOrManager
              ? 'Distribute leads to leaders using weighted algorithm'
              : 'Distribute leads to your team members'}
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab Content */}
      <div className="card">
        {activeTab === 'distribute' && (
          <div className="space-y-6">
            {/* Pool Selection */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Select Pool</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['new', 'old', 'recycled'].map((pool) => (
                  <button
                    key={pool}
                    type="button"
                    onClick={() => {
                      setPoolType(pool);
                      setShowPreview(false);
                      setPreview(null);
                    }}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      poolType === pool
                        ? 'border-tenq-gold bg-tenq-gold/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-left">
                      <div className="font-semibold text-gray-900 uppercase mb-1">
                        {pool} Pool
                      </div>
                      <div className="text-2xl font-bold text-tenq-gold">
                        {loadingStats ? (
                          <Spinner size="sm" />
                        ) : (
                          poolStats[pool] || 0
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Available leads
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Manual Selection Toggle */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Selection Mode</h3>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={manualSelectionEnabled}
                    onChange={(e) => {
                      setManualSelectionEnabled(e.target.checked);
                      if (e.target.checked) {
                        fetchPoolLeads();
                      } else {
                        setSelectedLeadIds([]);
                        setPoolLeads([]);
                      }
                    }}
                    className="w-4 h-4 text-tenq-gold focus:ring-tenq-gold"
                  />
                  <span className="text-gray-700 font-medium">Manual Lead Selection</span>
                </label>
                {manualSelectionEnabled && (
                  <span className="text-sm text-gray-500">
                    {selectedLeadIds.length} lead{selectedLeadIds.length !== 1 ? 's' : ''} selected
                  </span>
                )}
              </div>
            </div>

            {/* Lead Selector Component */}
            {manualSelectionEnabled && (
              loadingPoolLeads ? (
                <div className="text-center py-8">
                  <Spinner size="md" />
                  <p className="text-gray-500 mt-2">Loading pool leads...</p>
                </div>
              ) : (
                <LeadSelector
                  pool={poolLeads}
                  poolType={poolType}
                  selectedLeads={selectedLeadIds}
                  onSelectLead={(id) => {
                    setSelectedLeadIds(prev =>
                      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                    );
                  }}
                  onSelectQuantity={(ids) => setSelectedLeadIds(ids)}
                  onClearSelection={() => setSelectedLeadIds([])}
                />
              )
            )}

            {/* Lead Type (Director/Manager Only) */}
            {isDirectorOrManager && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Lead Type</h3>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="leadType"
                      value="team"
                      checked={leadType === 'team'}
                      onChange={(e) => setLeadType(e.target.value)}
                      className="w-4 h-4 text-tenq-gold focus:ring-tenq-gold"
                    />
                    <span className="text-gray-700">Team Leads (can be distributed to agents)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="leadType"
                      value="personal"
                      checked={leadType === 'personal'}
                      onChange={(e) => setLeadType(e.target.value)}
                      className="w-4 h-4 text-tenq-gold focus:ring-tenq-gold"
                    />
                    <span className="text-gray-700">Personal Leads (stays with leader)</span>
                  </label>
                </div>
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Team Leads:</strong> Leaders can distribute these to their agents.<br/>
                    <strong>Personal Leads:</strong> Reserved for the leader's personal follow-up, cannot be distributed further.
                  </p>
                </div>
              </div>
            )}

            {/* Weight Configurator */}
            {loadingRecipients ? (
              <div className="text-center py-8">
                <Spinner size="lg" />
              </div>
            ) : showPreview ? (
              <DistributionPreview
                preview={preview}
                onExecute={handleExecute}
                onCancel={() => setShowPreview(false)}
                loading={loadingExecute}
              />
            ) : (
              <>
                <WeightConfigurator
                  recipients={recipients}
                  weights={weights}
                  onChange={setWeights}
                />

                {/* Preview Button */}
                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handlePreview}
                    disabled={loadingPreview || Object.keys(weights).length === 0}
                    className="px-6 py-2 bg-tenq-gold text-white rounded-md hover:bg-tenq-gold-dark transition-colors disabled:opacity-50 font-semibold"
                  >
                    {loadingPreview ? 'Loading...' : 'Preview Distribution'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          loadingHistory ? (
            <div className="text-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <DistributionHistory
              sessions={history}
              onRefresh={handleRollbackComplete}
              loading={loadingRollback}
            />
          )
        )}
      </div>
    </>
  );
}
